import {
  CheckoutActorError,
  type CheckoutFailure,
} from "./contracts";

export const DEFAULT_CHECKOUT_ACTOR_TIMEOUT_MS = 20_000;

function asFailure(error: unknown): CheckoutFailure {
  if (error instanceof CheckoutActorError) return error.failure;
  if (error instanceof DOMException && error.name === "AbortError") {
    return {
      code: "aborted",
      message: "The operation was cancelled",
      retryable: true,
      cause: error,
    };
  }
  return {
    code: "network",
    message: error instanceof Error ? error.message : "The operation failed",
    retryable: true,
    cause: error,
  };
}

/**
 * Gives every invoked actor the same cancellation and timeout behavior. The
 * parent signal aborts the derived signal immediately when a state is exited,
 * preventing late results from a stale selection from committing.
 */
export async function runCheckoutActor<T>(
  parentSignal: AbortSignal,
  execute: (signal: AbortSignal) => Promise<T>,
  timeoutMs = DEFAULT_CHECKOUT_ACTOR_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort(parentSignal.reason);
  parentSignal.addEventListener("abort", abort, { once: true });
  const timeout = globalThis.setTimeout(() => {
    controller.abort(new DOMException("Timed out", "TimeoutError"));
  }, timeoutMs);

  try {
    return await execute(controller.signal);
  } catch (error) {
    if (
      controller.signal.reason instanceof DOMException &&
      controller.signal.reason.name === "TimeoutError"
    ) {
      throw new CheckoutActorError({
        code: "timeout",
        message: "The operation timed out",
        retryable: true,
        cause: error,
      });
    }
    throw new CheckoutActorError(asFailure(error));
  } finally {
    globalThis.clearTimeout(timeout);
    parentSignal.removeEventListener("abort", abort);
  }
}

export function checkoutFailure(error: unknown): CheckoutFailure {
  return error instanceof CheckoutActorError
    ? error.failure
    : asFailure(error);
}
